import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const platformData = {
    facebook: {
        title: "Facebook — Community + local pages monitoring",
        desc: "Track local community conversations, pages, and brand mentions relevant to your target city clusters. Useful for hyperlocal communities, local events, and outlet-level updates.",
        track: [
            "Local pages + outlet pages + competitor pages",
            "Keywords: “near me”, “opening”, “offer”, “review” + locality terms",
            "Community signals and engagement spikes by locality"
        ],
        use: [
            "Local event visibility and community sentiment",
            "Outlet-level buzz tracking and issue spotting",
            "Hyperlocal content themes for campaign scripts"
        ]
    },
    instagram: {
        title: "Instagram — Local creator + UGC signals",
        desc: "Monitor reels-driven buzz using locality hashtags, outlet handles, and creator tagging. Identify what is trending in a specific pocket and shortlist creators who are active locally.",
        track: [
            "Locality hashtags (#baner #wakad #pcmc) + category hashtags",
            "@outlet handles + competitor handles",
            "Mentions of offers, launches, events in captions/comments"
        ],
        use: [
            "Hyperlocal creator selection",
            "UGC prompt ideas + hooks",
            "Weekly content direction for ops + servicing"
        ]
    },
    youtube: {
        title: "YouTube — Long-form discoverability by city interest",
        desc: "Track creators who consistently publish city-specific content (food, real estate, colleges, events). Great for deeper consideration campaigns and locality storytelling.",
        track: [
            "City + locality keywords in titles/descriptions",
            "Creators covering local categories (food, education, real estate)",
            "Comment patterns that indicate local demand"
        ],
        use: [
            "Locality-specific storytelling campaigns",
            "Creator partnerships for deeper trust formats",
            "Trend validation before large-scale spends"
        ]
    },
    twitter: {
        title: "X (Twitter) — Real-time local pulse",
        desc: "Monitor fast-moving conversations around local incidents, events, launches, and sentiment shifts. Useful for rapid response and local PR alignment.",
        track: [
            "Brand + competitor mentions with city/locality terms",
            "Event hashtags and real-time spikes",
            "Local influencers/journalists posting about your category"
        ],
        use: [
            "Rapid response and issue tracking",
            "Local trend spotting for timely creatives",
            "Competitive monitoring during launches"
        ]
    },
    twitch: {
        title: "Twitch — Niche creator communities",
        desc: "Useful when your audience overlaps with gaming/streaming communities. Monitor creators by region and campaign keyword themes to identify collaboration pockets.",
        track: [
            "Streamer handles and community mentions",
            "Category keywords and branded themes",
            "Community engagement signals around events"
        ],
        use: [
            "Regional creator partnerships for niche audiences",
            "Event-driven activations and shout-outs",
            "Community-led UGC prompts"
        ]
    },
    linkedin: {
        title: "LinkedIn — Local professional communities",
        desc: "Monitor local business conversations, employer branding, education, real estate, and community initiatives. Ideal for B2B or premium positioning with locality context.",
        track: [
            "Company pages + local leaders + category keywords",
            "Location tags and local business initiatives",
            "High-signal posts relevant to city clusters"
        ],
        use: [
            "Local credibility and thought leadership themes",
            "Partnership discovery with local stakeholders",
            "Employer branding + community narratives"
        ]
    }
};

const MediaMonitor = () => {
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [activePlatform, setActivePlatform] = useState('instagram');

    const currentPlatform = platformData[activePlatform];

    return (
        <div className="bg-white text-slate-900 font-sans">
            <style>
                {`
          .glass { backdrop-filter: blur(10px); background: rgba(255,255,255,.82); }
          .shadow-soft { box-shadow: 0 14px 40px rgba(2,6,23,.08); }
          .ring-soft { box-shadow: 0 0 0 1px rgba(15,23,42,.06); }
        `}
            </style>

            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/80 glass">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-xl bg-slate-900"></div>
                            <span className="text-base font-semibold tracking-tight">Oye Creators</span>
                        </Link>

                        <nav className="hidden items-center gap-6 md:flex">
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="/product/discover-creators">Product</Link>
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="#">Solutions</Link>
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="#">Resources</Link>
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="/pricing-page">Pricing</Link>
                        </nav>

                        <div className="hidden items-center gap-3 md:flex">
                            <Link to="/signin" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Sign in</Link>
                            <a href="#cta" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
                                Request demo
                            </a>
                        </div>

                        <button
                            id="mobileBtn"
                            className="md:hidden rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold"
                            onClick={() => setMobileNavOpen(!mobileNavOpen)}
                        >
                            Menu
                        </button>
                    </div>

                    {/* Mobile Nav */}
                    {mobileNavOpen && (
                        <div id="mobileNav" className="border-t border-slate-100 py-4 md:hidden">
                            <div className="flex flex-col gap-2">
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="/product/discover-creators">Product</Link>
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="#">Solutions</Link>
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="#">Resources</Link>
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="/pricing-page">Pricing</Link>
                                <div className="mt-2 flex gap-2">
                                    <Link to="/signin" className="w-1/2 rounded-xl border border-slate-200 px-4 py-2 text-center text-sm font-semibold">Sign in</Link>
                                    <a href="#cta" className="w-1/2 rounded-xl bg-slate-900 px-4 py-2 text-center text-sm font-semibold text-white">Request demo</a>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Hero */}
            <section className="relative">
                <div className="mx-auto max-w-7xl px-4 pb-10 pt-12 sm:px-6 lg:px-8 lg:pb-16 lg:pt-16">
                    <div className="grid items-center gap-10 lg:grid-cols-2">
                        <div>
                            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700">
                                Media Monitor • Hyperlocal Intelligence
                            </div>

                            <h1 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
                                Monitor hyperlocal conversations across platforms—city by city
                            </h1>

                            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                                Oye Creators Media Monitor helps brands and agencies track local buzz—mentions, UGC, competitor activity,
                                and trends—by <span className="font-semibold text-slate-900">city, locality, pin-code, and language</span>.
                                Turn scattered platform activity into a single actionable feed for your campaign team.
                            </p>

                            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                                <a href="#platforms" className="rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 text-center">
                                    Explore platforms
                                </a>
                                <a href="#cta" className="rounded-2xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 text-center">
                                    Request demo
                                </a>
                            </div>

                            <div className="mt-8 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                <span className="rounded-full border border-slate-200 px-3 py-1">Locality-level monitoring</span>
                                <span className="rounded-full border border-slate-200 px-3 py-1">Hashtags + keywords + handles</span>
                                <span className="rounded-full border border-slate-200 px-3 py-1">UGC + creator spotting</span>
                                <span className="rounded-full border border-slate-200 px-3 py-1">Campaign-ready exports</span>
                            </div>

                            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                                Note: Platform coverage and available metrics depend on account permissions, platform policies, and your configured integrations.
                            </div>
                        </div>

                        <div>
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100 shadow-soft"></div>
                            <p className="mt-3 text-xs text-slate-500">
                                Replace with screenshot: “Hyperlocal feed view + locality filters + post cards + export”.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hyperlocal explainer */}
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                    <div className="grid gap-8 lg:grid-cols-3">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Why hyperlocal</div>
                            <h2 className="mt-2 text-2xl font-semibold tracking-tight">Campaign decisions change by locality</h2>
                            <p className="mt-3 text-slate-600">
                                Aundh is not Wakad. Baner is not Kothrud. The content, language, creators, and offers that work in one pocket
                                often fail in another. Media Monitor helps you observe local signals before you plan and execute.
                            </p>
                        </div>

                        <div className="rounded-3xl border border-slate-100 bg-slate-50 p-6">
                            <div className="text-sm font-semibold">Typical tracking inputs</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-700">
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>City / Area / Locality / Pin-code</li>
                                <li className="flex gap-3"><span class="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Handles (brand + competitor + outlets)</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Hashtags (#brand #location #category)</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Keywords (Hindi/Marathi/English mix)</li>
                            </ul>
                        </div>

                        <div className="rounded-3xl border border-slate-100 bg-slate-50 p-6">
                            <div className="text-sm font-semibold">Typical outputs</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-700">
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Local content trends & hooks</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>UGC winners & repurpose list</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Creator discovery signals (who is active locally)</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Competitor activity tracking</li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-3xl border border-slate-100 bg-white p-6 lg:flex-row lg:items-center">
                        <div>
                            <div className="text-sm font-semibold">Example: Locality feed</div>
                            <p className="mt-1 text-sm text-slate-600">
                                “Pune • Baner • #cafe #brunch #workfromcafe + competitor handles + outlet mentions”
                            </p>
                        </div>
                        <a href="#cta" className="rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                            Set up a hyperlocal feed
                        </a>
                    </div>
                </div>
            </section>

            {/* Platforms (explanatory + hyperlocal use-cases) */}
            <section id="platforms" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <h2 className="text-3xl font-semibold tracking-tight">Platform-wise hyperlocal monitoring</h2>
                            <p className="mt-2 max-w-3xl text-slate-600">
                                Use one monitoring layer across platforms, while keeping the playbook hyperlocal:
                                local language, local context, local creators, and local conversion objectives.
                            </p>
                        </div>
                        <div className="text-xs text-slate-500">
                            Tip: Configure separate feeds per city or per cluster (e.g., “PCMC”, “Pune Central”, “East Pune”).
                        </div>
                    </div>

                    {/* Platform chips */}
                    <div className="mt-6 flex flex-wrap gap-2">
                        {Object.keys(platformData).map((plat) => (
                            <button
                                key={plat}
                                className={`rounded-full border px-4 py-2 text-sm font-semibold hover:bg-slate-50 transition-colors ${activePlatform === plat
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white text-slate-900 border-slate-200'
                                    }`}
                                onClick={() => setActivePlatform(plat)}
                            >
                                {plat.charAt(0).toUpperCase() + plat.slice(1).replace('twitter', 'X (Twitter)')}
                            </button>
                        ))}
                    </div>

                    {/* Platform panels */}
                    <div className="mt-6 grid gap-6 lg:grid-cols-3">
                        <div className="lg:col-span-2 rounded-3xl border border-slate-100 bg-slate-50 p-6">
                            <div className="text-lg font-semibold">{currentPlatform.title}</div>
                            <p className="mt-2 text-sm text-slate-700">
                                {currentPlatform.desc}
                            </p>

                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                <div className="rounded-2xl border border-slate-100 bg-white p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Track</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        {currentPlatform.track.map((item, idx) => (
                                            <li key={idx} className="flex gap-3">
                                                <span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>{item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="rounded-2xl border border-slate-100 bg-white p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Use it for</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        {currentPlatform.use.map((item, idx) => (
                                            <li key={idx} className="flex gap-3">
                                                <span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>{item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a href="#cta" className="rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 text-center">
                                    Request platform demo
                                </a>
                                <a href="#features" className="rounded-2xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 text-center">
                                    See monitoring features
                                </a>
                            </div>
                        </div>

                        {/* Sidebar: hyperlocal presets */}
                        <div className="rounded-3xl border border-slate-100 bg-white p-6">
                            <div className="text-sm font-semibold">Hyperlocal feed presets</div>
                            <p className="mt-2 text-sm text-slate-600">Use these presets to go live quickly, then tune keywords and handles.</p>

                            <div className="mt-4 space-y-3">
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-sm font-semibold">Outlet Launch Monitor</div>
                                    <div className="mt-1 text-xs text-slate-600">Track: locality hashtags + brand handle + “new/launch/opening”.</div>
                                </div>
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-sm font-semibold">Competitor Watch</div>
                                    <div className="mt-1 text-xs text-slate-600">Track: competitor handle + tagged creators + locality keywords.</div>
                                </div>
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-sm font-semibold">UGC Hashtag Feed</div>
                                    <div className="mt-1 text-xs text-slate-600">Track: campaign hashtag + city/locality + category terms.</div>
                                </div>
                            </div>

                            <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reminder</div>
                                <p className="mt-2 text-sm text-slate-600">
                                    Ensure your feeds reflect your campaign geography: city clusters, local language, and specific outlet handles.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:gap-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-semibold tracking-tight">Built for hyperlocal campaign teams</h2>
                        <p className="mx-auto mt-3 max-w-2xl text-slate-600">
                            Monitor → shortlist → brief → activate. Keep everything tied to locality signals so your creative and creator selection stays relevant.
                        </p>
                    </div>

                    {/* Feature 1 */}
                    <div className="grid items-center gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:grid-cols-2 lg:p-10">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Local mentions</div>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight">Track brand mentions by locality and language</h3>
                            <p className="mt-3 text-slate-600">
                                Monitor local conversations using handles + keywords (Hindi/Marathi/English mix). Identify what people like, ask, or complain about—fast.
                            </p>
                            <a href="#cta" className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                                Track local mentions
                            </a>
                        </div>
                        <div>
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100"></div>
                        </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="grid items-center gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:grid-cols-2 lg:p-10">
                        <div className="order-2 lg:order-1">
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100"></div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trends</div>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight">Catch locality-specific trends early</h3>
                            <p className="mt-3 text-slate-600">
                                Identify what is trending in your target pockets—music, hooks, formats, and category conversations. Convert signals into briefs for creators.
                            </p>
                            <a href="#cta" className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                                Track trends
                            </a>
                        </div>
                    </div>

                    {/* Feature 3 */}
                    <div className="grid items-center gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:grid-cols-2 lg:p-10">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">UGC</div>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight">Monitor UGC for campaign hashtags</h3>
                            <p className="mt-3 text-slate-600">
                                Track UGC participation and discover high-performing posts for repurposing. Create a “winner shortlist” for paid boosting or brand pages.
                            </p>
                            <a href="#cta" className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                                Monitor UGC
                            </a>
                        </div>
                        <div>
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100"></div>
                        </div>
                    </div>

                    {/* Feature 4 */}
                    <div className="grid items-center gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:grid-cols-2 lg:p-10">
                        <div className="order-2 lg:order-1">
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100"></div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Competitors</div>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight">Track competitor activity in your pockets</h3>
                            <p className="mt-3 text-slate-600">
                                Observe competitor posting patterns, creator tagging, and locality engagement signals. Use insights to refine your hyperlocal campaign plan.
                            </p>
                            <a href="#cta" className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                                Monitor competitors
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-8">
                <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                    <h2 className="text-2xl font-semibold tracking-tight">FAQs</h2>
                    <p className="mt-2 text-slate-600">
                        Platform references and monitoring capabilities depend on integrations and policy constraints.
                    </p>

                    <div className="mt-6 divide-y divide-slate-100">
                        <details className="group py-4" open>
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                How does Oye Creators define “hyperlocal” monitoring?
                                <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
                            </summary>
                            <p className="mt-3 text-sm text-slate-600">
                                Hyperlocal means tracking by city clusters and locality signals: area keywords, outlet handles, pin-code references, and local language.
                                You should set up separate feeds per geography to keep insights clean.
                            </p>
                        </details>

                        <details className="group py-4">
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                Can I track Facebook, Instagram, YouTube, X (Twitter), Twitch, and LinkedIn?
                                <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
                            </summary>
                            <p className="mt-3 text-sm text-slate-600">
                                Yes, you can structure monitoring by platform. Actual data availability depends on platform permissions and your configured integrations.
                                During the demo, we map your requirement (mentions/keywords/hashtags/accounts) to what is feasible per platform.
                            </p>
                        </details>

                        <details className="group py-4">
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                What is the best setup for multi-city campaigns?
                                <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
                            </summary>
                            <p className="mt-3 text-sm text-slate-600">
                                Create one feed per city/cluster (e.g., Pune, PCMC, Mumbai West, Mumbai Central). Use outlet handles + locality hashtags + category keywords.
                                Then consolidate weekly insights into a single brief for Ops and servicing.
                            </p>
                        </details>

                        <details className="group py-4">
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                How does this connect to creator campaigns?
                                <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
                            </summary>
                            <p className="mt-3 text-sm text-slate-600">
                                Media Monitor helps you identify winning local hooks and active local creators. You can then shortlist creators, craft briefs, and execute campaigns using Oye Creators workflows.
                            </p>
                        </details>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section id="cta" className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                        <h3 className="text-xl font-semibold tracking-tight">Get a demo for your city clusters</h3>
                        <p className="mt-2 text-slate-600">
                            We will configure a sample hyperlocal feed using your target cities, localities, keywords, and handles—then show how teams convert insights into briefs.
                        </p>
                        <button className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 border-none cursor-pointer">
                            Request demo
                        </button>
                    </div>

                    <div className="rounded-3xl bg-slate-900 p-6 text-white shadow-soft lg:p-10">
                        <h3 class="text-xl font-semibold tracking-tight">Start with one locality feed</h3>
                        <p className="mt-2 text-white/80">
                            Launch one feed for one locality first. Once it works, scale to multiple city clusters and campaign categories.
                        </p>
                        <button className="mt-5 inline-flex items-center rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100 border-none cursor-pointer">
                            Try it now
                        </button>
                    </div>
                </div>

                {/* Logo strip */}
                <div className="mt-10 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                    <p className="text-center text-sm font-semibold text-slate-700">
                        Trusted by teams running high-volume hyperlocal creator programs
                    </p>
                    <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                        <div className="h-10 rounded-xl bg-slate-100"></div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-slate-100">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
                    <div className="grid gap-10 md:grid-cols-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="h-9 w-9 rounded-xl bg-slate-900"></div>
                                <div className="text-base font-semibold">Oye Creators</div>
                            </div>
                            <p className="mt-3 text-sm text-slate-600">
                                Hyperlocal creator campaigns with measurable outcomes—powered by data.
                            </p>
                        </div>

                        <div>
                            <div className="text-sm font-semibold">Product</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                <li><Link className="hover:text-slate-900" to="/product/discover-creators">Discovery</Link></li>
                                <li><Link className="hover:text-slate-900" to="/product/media-analytics">Market Analysis</Link></li>
                                <li><Link className="hover:text-slate-900" to="/product/media-plans">Media Plans</Link></li>
                                <li><Link className="hover:text-slate-900" to="/product/manage-campaigns">Outreach</Link></li>
                                <li><Link className="hover:text-slate-900" to="#">Reporting</Link></li>
                            </ul>
                        </div>

                        <div>
                            <div className="text-sm font-semibold">Resources</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                <li><Link className="hover:text-slate-900" to="#">Docs</Link></li>
                                <li><Link className="hover:text-slate-900" to="#">Case Studies</Link></li>
                                <li><Link className="hover:text-slate-900" to="#">Support</Link></li>
                            </ul>
                        </div>

                        <div>
                            <div className="text-sm font-semibold">Legal</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                <li><Link className="hover:text-slate-900" to="/terms-of-service">Terms</Link></li>
                                <li><Link className="hover:text-slate-900" to="/privacy-policy">Privacy</Link></li>
                                <li><Link className="hover:text-slate-900" to="/cookies-and-ip">Cookie Policy</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-10 text-xs text-slate-500">
                        © <span id="year">{new Date().getFullYear()}</span> Oye Creators. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default MediaMonitor;
